import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { Link } from "./Link";
import { ChevronIcon } from "./ChevronIcon";
import { Accordion, AccordionItem } from "./Accordion";
import { SimpleRichTextProps } from "./SimpleRichText";

export interface ListItemBase {
  icon?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
}

export interface ListItem extends ListItemBase {
  type: "listItem";
  text?: string;
  labelColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
}

export interface ListAccordionItem {
  type: "accordionItem";
  title?: string;
  text?: Array<{
    content: React.ReactNode;
    size?: "small" | "medium" | "large";
    fontColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
  }>;
  backgroundColor?: "none" | "light" | "accent" | "accent-light";
}

export interface ListLinkItem extends ListItemBase {
  type: "listLink";
  text?: string;
  href?: string;
  target?: string;
  noFollow?: boolean;
}

export type ListItemType = ListItem | ListAccordionItem | ListLinkItem;

export interface ListProps extends Omit<ComponentProps<"div">, "title"> {
  items?: ListItemType[];
  onItemToggle?: (index: number, title: string, isOpen: boolean) => void;
}

const ListItemComponent: React.FC<{ item: ListItem; index: number }> = ({
  item,
  index,
}) => {
  const labelColorClass = {
    default: "uipm:text-[var(--typography-colors-text-body)]",
    accent: "uipm:text-[var(--typography-colors-text-accent)]",
    faded: "uipm:text-[var(--typography-colors-text-faded)]",
    "faded-dark": "uipm:text-[var(--typography-colors-text-faded-dark)]",
    inverted: "uipm:text-[var(--typography-colors-text-inverted)]",
  }[item.labelColor || "faded-dark"];

  return (
    <div
      key={index}
      className="uipm:flex uipm:items-center uipm:gap-[var(--margin-margin-sm)]"
    >
      {item.icon && (
        <div className="uipm:relative uipm:w-8 uipm:h-8 uipm:flex-shrink-0">
          <Image
            src={item.icon.src}
            alt={item.icon.alt}
            width={32}
            height={32}
            className="uipm:object-contain"
          />
        </div>
      )}
      {item.text && (
        <div className={cn("uipm:flex-1 uipm:self-center", labelColorClass)}>
          <span
            className={cn(
              "uipm:text-[length:var(--typography-sizes-body-medium)]",
              "uipm:leading-[var(--typography-line-heights-body-medium)]",
            )}
          >
            {item.text}
          </span>
        </div>
      )}
    </div>
  );
};

const convertTextToSimpleRichTextProps = (
  textArray?: Array<{
    content: React.ReactNode;
    size?: "small" | "medium" | "large";
    fontColor?: "default" | "accent" | "faded" | "faded-dark" | "inverted";
  }>,
): SimpleRichTextProps | null => {
  if (!textArray || textArray.length === 0) return null;

  return {
    children: (
      <>
        {textArray.map((textItem, index) => (
          <div
            key={index}
            className={cn(
              "uipm:text-[var(--typography-colors-text-default)]",
              textItem.size === "small" &&
                "uipm:text-[length:var(--typography-sizes-body-small)] uipm:leading-[var(--typography-line-heights-body-small)]",
              textItem.size === "medium" &&
                "uipm:text-[length:var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)]",
              textItem.size === "large" &&
                "uipm:text-[length:var(--typography-sizes-body-large)] uipm:leading-[var(--typography-line-heights-body-large)]",
              !textItem.size &&
                "uipm:text-[length:var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)]",
              textItem.fontColor === "accent" &&
                "uipm:text-[var(--typography-colors-text-accent)]",
              textItem.fontColor === "faded" &&
                "uipm:text-[var(--typography-colors-text-faded)]",
              textItem.fontColor === "faded-dark" &&
                "uipm:text-[var(--typography-colors-text-faded-dark)]",
              textItem.fontColor === "inverted" &&
                "uipm:text-[var(--typography-colors-text-inverted)]",
            )}
          >
            {textItem.content}
          </div>
        ))}
      </>
    ),
    size: textArray[0]?.size || "medium",
    fontColor: textArray[0]?.fontColor || "default",
  };
};

const ListLinkComponent: React.FC<{ item: ListLinkItem; index: number }> = ({
  item,
  index,
}) => {
  const content = (
    <>
      {item.icon && (
        <div className="uipm:relative uipm:w-8 uipm:h-8 uipm:flex-shrink-0">
          <Image
            src={item.icon.src}
            alt={item.icon.alt}
            width={32}
            height={32}
            className="uipm:object-contain"
          />
        </div>
      )}
      {item.icon && item.text && <div className="uipm:w-[24px]" />}
      {item.text && (
        <div className="uipm:flex-1">
          <span
            className={cn(
              "uipm:text-[var(--typography-colors-text-accent)]",
              "uipm:text-[var(--typography-sizes-headline-6)]",
              "uipm:leading-[var(--typography-line-heights-body-large)]",
              "uipm:font-semibold",
            )}
          >
            {item.text}
          </span>
        </div>
      )}
      <div className="uipm:w-[24px]" />
      <ChevronIcon
        className="uipm:w-6 uipm:h-6 uipm:rotate-0"
        size={24}
        color="var(--color-color-accent)"
      />
    </>
  );

  const containerClasses = cn(
    "uipm:w-full",
    "uipm:bg-[var(--surface-surface-secondary)]",
    "uipm:rounded-[var(--radius-radius-sm)]",
    "uipm:py-[var(--padding-padding-sm-plus)] uipm:px-[var(--padding-padding-default)]",
    "uipm:flex uipm:items-center",
    "uipm:transition-colors",
    "hover:uipm:bg-[var(--surface-surface-light)]",
  );

  if (item.href) {
    return (
      <Link
        key={index}
        href={item.href}
        target={item.target}
        className={containerClasses}
        noFollow={item.noFollow}
      >
        {content}
      </Link>
    );
  }

  return (
    <div key={index} className={containerClasses}>
      {content}
    </div>
  );
};

export const List: React.FC<ListProps> = ({
  items,
  className,
  onItemToggle,
  ...props
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  const accordionItems: AccordionItem[] = [];
  const accordionIndices: number[] = [];

  items.forEach((item, index) => {
    if (item.type === "accordionItem") {
      const textProps = convertTextToSimpleRichTextProps(item.text);
      accordionItems.push({
        title: item.title || "",
        text: textProps ? [textProps] : [],
        backgroundColor: item.backgroundColor,
      });
      accordionIndices.push(index);
    }
  });

  // If all items are accordion items, use the Accordion component directly
  if (accordionItems.length === items.length) {
    return (
      <Accordion
        items={accordionItems}
        variant="compact"
        className={className}
        onItemToggle={onItemToggle}
        {...props}
      />
    );
  }

  // Mixed list - render each item type appropriately
  let accordionIndex = 0;

  return (
    <div
      className={cn(
        "uipm:flex uipm:flex-col uipm:gap-4 uipm:w-full",
        className,
      )}
      {...props}
    >
      {items.map((item, index) => {
        switch (item.type) {
          case "listItem":
            return <ListItemComponent key={index} item={item} index={index} />;
          case "accordionItem":
            // Use single-item Accordion for consistency
            const currentAccordionItem = accordionItems[accordionIndex];
            const currentOriginalIndex = accordionIndices[accordionIndex];
            accordionIndex++;
            if (!currentAccordionItem) return null;
            return (
              <Accordion
                key={index}
                items={[currentAccordionItem]}
                variant="compact"
                defaultOpenIndex={undefined}
                onItemToggle={
                  onItemToggle && currentOriginalIndex !== undefined
                    ? (itemIndex, title, isOpen) => {
                        onItemToggle(currentOriginalIndex, title, isOpen);
                      }
                    : undefined
                }
              />
            );
          case "listLink":
            return <ListLinkComponent key={index} item={item} index={index} />;
          default:
            return null;
        }
      })}
    </div>
  );
};
